// Define the structure of the input data from Formly
interface FormlyInput {
  transitionDetails: {
    startState?: string;
    name?: string;
  };
  endStateOne?: {
    stateName?: string | null;
    stateCode?: string | null;
    nextEvent?: string | null;
  };
  endStateTwo?: {
    stateName: string | null;
    stateCode: string | null;
    nextEvent?: string | null;
  };
  authorisation?: FormlyAuthorisation;
}

export interface FormlyAuthorisation {
  authMode?: 'USER_AUTHORIZED' | 'SYSTEM_AUTHORIZED';
  roleBased?: boolean;
  anyAuthenticatedUser?: boolean;
  userBased?: boolean;
  authorisedRoles?: string[];
  authorisedUsers?: string[];
}

export interface Authorisation {
  authorisedRoles: string[];
  authorisedUsers: string[];
  systemAuthorised: boolean;
}

// Define the structure of the DTO
export interface Dto {
  startState: string | null;
  event: string | null;
  endStateOne: EndState | null;
  endStateTwo: EndState | null;
  endStateCondition: any;
  authorisation: Authorisation;
}

interface EndState {
  stateName: string | null;
  stateCode: string | null;
  breakingAction: ActionArgs | null;
  breakingActions: stateAction[] | null;
  nonBreakingActions: stateAction[] | null;
  nextEvent: string | null;
}

interface stateAction {
  actionType: string;
  args: ActionArgs | null;
}

interface ActionArgs {
  notificationType: string;
  dynamicTemplate: DynamicTemplate | null;
  staticTemplate: StaticTemplate;
  attachment: string | null;
  integrationTargetId: string | null;
  endpointCode: string | null;
  async: boolean | null;
  officeAssignmentType: string | null;
  formFieldKeys: string | null;
  officeLevel: string | null;
  formFieldKey: string | null;
  fixedOfficeCode: string | null;
  officeAssignmentExpression: string | null;
  officeId: string | null;
  certificateName: string | null;
  certificateTemplateCode: string | null;
  certificateExpirationDays: number | null;
  certificateWithList: boolean;
  fetchValueAttribute: string | null;
  fetchValueTypeAttribute: string | null;
  scheduleCode: string | null;
  scheduleDate: string | null;
  scheduleDateFormat: string | null;
  serviceCode: string | null;
  applyPayload: Record<string, string> | null;
}

interface StaticTemplate {
  [language: string]: {
    title: string | null;
    subject: string | null;
    emailBody: string | null;
    smsBody: string | null;
  };
}

interface DynamicTemplate {
  [language: string]: {
    emailTemplateName: string | null;
    smsTemplateName: string | null;
  };
}

function cleanupStaticTemplate(template: StaticTemplate): StaticTemplate {
  const cleanedTemplate: StaticTemplate = {};

  for (const [language, content] of Object.entries(template)) {
    const isComplete =
      content.title !== null &&
      content.subject !== null &&
      content.emailBody !== null &&
      content.smsBody !== null;

    if (isComplete) {
      cleanedTemplate[language] = content;
    }
  }

  return cleanedTemplate;
}

function mapAction(action: {
  actionType: string;
  args?: ActionArgs | null;
}): stateAction {
  if (
    action.actionType === 'NOTIFICATION' &&
    action.args?.notificationType === 'STATIC'
  ) {
    const cleanedStaticTemplate = cleanupStaticTemplate(
      action.args.staticTemplate
    );
    return {
      actionType: action.actionType,
      args: {
        ...action.args,
        staticTemplate: cleanedStaticTemplate,
      },
    };
  }

  // Process APPLY_SERVICE action type
  if (action.actionType === 'APPLY_SERVICE' && action.args) {
    // Convert applyPayload array to object if it exists
    if (action.args.applyPayload && Array.isArray(action.args.applyPayload)) {
      const payloadObject: Record<string, string> = {};

      (action.args.applyPayload as any[]).forEach(mapping => {
        if (mapping.key && mapping.value) {
          payloadObject[mapping.key] = '$.' + `${mapping.value}`;
        }
      });

      return {
        actionType: action.actionType,
        args: {
          ...action.args,
          applyPayload: payloadObject,
        },
      };
    }
  }

  return {
    actionType: action.actionType,
    args: action.args ?? null,
  };
}

// Utility function to create an EndState or return null if all properties are null
function createEndState(endStateInput?: {
  stateName?: string | null;
  stateCode?: string | null;
  nextEvent?: string | null;
  nonBreakingActions?: stateAction[] | null;
  breakingActions?: stateAction[] | null;
}): EndState | null {
  if (
    !endStateInput ||
    ((endStateInput.stateName === null ||
      endStateInput.stateName === undefined) &&
      (endStateInput.stateCode === null ||
        endStateInput.stateCode === undefined) &&
      (!endStateInput.nonBreakingActions ||
        endStateInput.nonBreakingActions.length === 0) &&
      (!endStateInput.breakingActions ||
        endStateInput.breakingActions.length === 0))
  ) {
    return null;
  }

  const hasActions =
    (endStateInput.nonBreakingActions?.length ?? 0) > 0 ||
    (endStateInput.breakingActions?.length ?? 0) > 0;

  const hasStateInfo =
    endStateInput.stateCode != null || endStateInput.stateName != null;

  if (hasActions && !hasStateInfo) {
    throw new Error(
      'EndState must have both stateCode and stateName if actions are provided'
    );
  }

  if (!hasStateInfo && !hasActions) {
    return null;
  }

  // Map nonBreakingActions if they exist
  const mappedNonBreakingActions: stateAction[] | null =
    endStateInput.nonBreakingActions
      ? endStateInput.nonBreakingActions.map(mapAction)
      : null;

  // Map breakingActions if they exist
  const mappedBreakingActions: stateAction[] | null =
    endStateInput.breakingActions
      ? endStateInput.breakingActions.map(mapAction)
      : null;

  return {
    stateName: endStateInput.stateName ?? null,
    stateCode: endStateInput.stateCode ?? null,
    breakingAction: null,
    breakingActions: mappedBreakingActions,
    nonBreakingActions: mappedNonBreakingActions,
    nextEvent: endStateInput.nextEvent ?? null,
  };
}

function buildAuthorisation(
  input: FormlyAuthorisation | undefined,
  errors: string[]
): Authorisation {
  const auth = input ?? {};
  const isSystem = auth.authMode === 'SYSTEM_AUTHORIZED';

  if (!isSystem && !auth.roleBased && !auth.userBased) {
    errors.push(
      'Authorisation: select Role Based and/or User Based, or switch to System Authorized'
    );
  }

  let outRoles: string[] = [];
  if (!isSystem && auth.roleBased) {
    if (auth.anyAuthenticatedUser) {
      outRoles = ['ANY_ROLE'];
    } else {
      outRoles = auth.authorisedRoles ?? [];
      if (outRoles.length === 0) {
        errors.push(
          'Authorisation: select at least one role or check "Any authenticated user"'
        );
      }
    }
  }

  let outUsers: string[] = [];
  if (!isSystem && auth.userBased) {
    outUsers = auth.authorisedUsers ?? [];
    if (outUsers.length === 0) {
      errors.push('Authorisation: select at least one user');
    }
  }

  return {
    authorisedRoles: outRoles,
    authorisedUsers: outUsers,
    systemAuthorised: !!isSystem,
  };
}

export function convertFormlyDataToDto(input: FormlyInput): {
  dto: Dto | null;
  errors: string[];
} {
  const errors: string[] = [];

  let endStateOne: EndState | null = null;
  let endStateTwo: EndState | null = null;

  try {
    endStateOne = createEndState(input.endStateOne);
  } catch (error) {
    if (error instanceof Error) {
      errors.push(`EndStateOne: ${error.message}`);
    }
  }

  try {
    endStateTwo = createEndState(input.endStateTwo);
  } catch (error) {
    if (error instanceof Error) {
      errors.push(`EndStateTwo: ${error.message}`);
    }
  }

  const authorisation = buildAuthorisation(input.authorisation, errors);

  if (errors.length > 0) {
    return { dto: null, errors };
  }

  return {
    dto: {
      startState: input.transitionDetails?.startState ?? null,
      event: input.transitionDetails?.name ?? null,
      endStateOne,
      endStateTwo,
      endStateCondition: null, // todo: Add Condition Handling through Form
      authorisation,
    },
    errors: [],
  };
}
